import type { VocabularySnapshot } from "../../domain/vocabulary/types.ts";
import type { ProductErrorCode } from "../../contracts/errors.ts";
import type {
  MigrationIssue,
  MigrationReport,
} from "../../contracts/storage/migrations/types.ts";

export type StorageWriteOrigin = "load" | "commit" | "replace" | "migrate";

export type PreMigrationCopyInfo = {
  sourceVersion: number;
  targetVersion: number;
  migratedAt: string;
  byteSize: number;
};

export type ReadyStorageStatus = {
  kind: "ready";
  snapshot: VocabularySnapshot;
  origin: StorageWriteOrigin;
  lastReport?: MigrationReport;
  preMigrationCopy?: PreMigrationCopyInfo;
};

export type EmptyStorageStatus = {
  kind: "empty";
  snapshot: VocabularySnapshot;
  preMigrationCopy?: PreMigrationCopyInfo;
};

export type UnavailableStorageStatus = {
  kind: "unavailable";
  message: string;
};

export type InvalidStorageStatus = {
  kind: "invalid";
  raw: string;
  reason: "malformed_json" | "invalid_version" | "invalid_source" | "empty_bytes" | "invalid_wrapper";
  message: string;
  issues?: MigrationIssue[];
};

export type UnsupportedStorageStatus = {
  kind: "unsupported";
  raw: string;
  version: number;
  direction: "future" | "old";
  message: string;
};

export type MigrationFailedStorageStatus = {
  kind: "migration_failed";
  raw: string;
  message: string;
  retryable: boolean;
  stage: "conversion" | "recovery_copy" | "primary_write";
  issues?: MigrationIssue[];
  preMigrationCopy?: PreMigrationCopyInfo;
};

export type MigrationBlockedStorageStatus = {
  kind: "migration_blocked";
  raw: string;
  sourceVersion: number;
  message: string;
};

export type StorageStatus =
  | ReadyStorageStatus
  | EmptyStorageStatus
  | UnavailableStorageStatus
  | InvalidStorageStatus
  | UnsupportedStorageStatus
  | MigrationFailedStorageStatus
  | MigrationBlockedStorageStatus;

export type LoadResult = StorageStatus;

export type CommitResult =
  | { ok: true; snapshot: VocabularySnapshot }
  | { ok: false; code: ProductErrorCode; message: string };

export type ReplaceAllOptions = {
  expectedRevision?: number;
};

export function isUsableStorageStatus(
  status: StorageStatus,
): status is ReadyStorageStatus | EmptyStorageStatus {
  return status.kind === "ready" || status.kind === "empty";
}

export function storageStatusRaw(status: StorageStatus): string | null {
  if ("raw" in status) {
    return status.raw;
  }
  return null;
}

export interface VocabularyStore {
  load(): Promise<LoadResult>;
  reload(): Promise<LoadResult>;
  getSnapshot(): VocabularySnapshot | null;
  getStatus(): StorageStatus;
  subscribe(listener: (snapshot: VocabularySnapshot) => void): () => void;
  subscribeStatus(listener: (status: StorageStatus) => void): () => void;
  commit(
    mutator: (current: VocabularySnapshot) => VocabularySnapshot,
  ): Promise<CommitResult>;
  replaceAll(snapshot: VocabularySnapshot, options?: ReplaceAllOptions): Promise<CommitResult>;
  exportRaw(): Promise<string | null>;
  exportPreMigrationCopy(): Promise<string | null>;
  removePreMigrationCopy(): Promise<CommitResult>;
  estimateBytes(snapshot: VocabularySnapshot): number;
  isWritable(): boolean;
}
